// src/authpages/group/VideoCallService.js
import { db } from '../../firebase';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  onSnapshot, 
  deleteDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';

// Configuration for WebRTC
const configuration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    { urls: 'stun:stun3.l.google.com:19302' },
    { urls: 'stun:stun4.l.google.com:19302' },
  ]
};

// Video/Audio constraints
export const mediaConstraints = {
  video: {
    width: { ideal: 640 }, // Lower resolution is more reliable
    height: { ideal: 480 },
    facingMode: 'user'
  },
  audio: true
};

export class VideoCallService {
  constructor(groupId, userId) {
    this.groupId = groupId;
    this.userId = userId;
    this.peerConnections = {};
    this.localStream = null;
    this.remoteStreams = {};
    this.onRemoteStreamHandler = null;
    this.onRemoteUserDisconnectedHandler = null;
    this.signalingCollection = collection(db, 'videoCallSignaling');
    this.roomRef = doc(collection(db, 'videoCallRooms'), groupId);
    this.callActive = false;
  }

  // Set callback handlers
  setOnRemoteStream(handler) {
    this.onRemoteStreamHandler = handler;
  }

  setOnRemoteUserDisconnected(handler) {
    this.onRemoteUserDisconnectedHandler = handler;
  }

  // Get local media stream with enhanced error handling
  async getLocalStream(deviceId = null) {
    try {
      // Use the simplest possible constraints at first
      const constraints = {
        audio: true,
        video: deviceId ? 
          { deviceId: { exact: deviceId } } : 
          true
      };
      
      console.log("Using simplified constraints:", constraints);
      
      // Try to get media with these constraints
      this.localStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      // Log detailed info about the stream
      const videoTracks = this.localStream.getVideoTracks();
      const audioTracks = this.localStream.getAudioTracks();
      
      console.log(`Got stream with ${videoTracks.length} video tracks and ${audioTracks.length} audio tracks`);
      
      if (videoTracks.length > 0) {
        const track = videoTracks[0];
        console.log(`Using video device: ${track.label}`);
        console.log(`Video track settings:`, track.getSettings());
      } else {
        console.warn("No video tracks in stream!");
      }
      
      return this.localStream;
    } catch (initialError) {
      console.error("Error with initial constraints:", initialError);
      
      // First fallback - try with just video: true
      try {
        console.log("Trying fallback with basic {video: true}");
        this.localStream = await navigator.mediaDevices.getUserMedia({
          audio: true, 
          video: true
        });
        return this.localStream;
      } catch (error) {
        console.error("Error getting local stream with both fallbacks:", error);
        
        // Provide more specific error messages based on the error name
        if (error.name === 'NotAllowedError') {
          throw new Error("Camera access denied. Please allow camera access in your browser and try again.");
        } else if (error.name === 'NotFoundError') {
          throw new Error("No camera detected. Please connect a camera and try again.");
        } else if (error.name === 'NotReadableError') {
          throw new Error("Camera is already in use by another application. Please close other apps using your camera.");
        } else {
          throw error;
        }
      }
    }
  }

  // Initialize and start the call
  async startCall() {
    try {
      // Create or join the room
      const roomSnapshot = await getDoc(this.roomRef);
      
      if (!roomSnapshot.exists()) {
        // Create new room if it doesn't exist
        await setDoc(this.roomRef, {
          createdBy: this.userId,
          createdAt: serverTimestamp(),
          activeParticipants: [this.userId],
          groupId: this.groupId // Adding groupId for security rules
        });
        console.log("Created new video call room");
      } else {
        // Join existing room
        const roomData = roomSnapshot.data();
        
        // Add current user to participants if not already there
        await updateDoc(this.roomRef, {
          activeParticipants: arrayUnion(this.userId)
        });
        
        // Connect to existing participants
        const participants = roomData.activeParticipants || [];
        for (const participantId of participants) {
          if (participantId !== this.userId) {
            await this.createPeerConnection(participantId);
            await this.createOffer(participantId);
          }
        }
      }
      
      // Listen for signaling messages
      this.setupSignalingListener();
      this.callActive = true;
      
      // Return the room data for UI use
      return roomSnapshot.exists() ? roomSnapshot.data() : { createdBy: this.userId };
    } catch (error) {
      console.error("Error starting call:", error);
      throw error;
    }
  }

  // Create a WebRTC peer connection with another user
  async createPeerConnection(remoteUserId) {
    try {
      const peerConnection = new RTCPeerConnection(configuration);
      
      // Store the connection
      this.peerConnections[remoteUserId] = peerConnection;
      
      // Add local tracks to the connection
      if (this.localStream) {
        this.localStream.getTracks().forEach(track => {
          peerConnection.addTrack(track, this.localStream);
        });
      }
      
      // Handle ICE candidates
      peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          this.sendSignalingMessage(remoteUserId, {
            type: 'ice-candidate',
            candidate: event.candidate.toJSON(),
            from: this.userId,
            to: remoteUserId
          });
        }
      };
      
      // Handle connection state changes
      peerConnection.onconnectionstatechange = (event) => {
        console.log(`Connection state change with ${remoteUserId}:`, peerConnection.connectionState);
        
        if (peerConnection.connectionState === 'disconnected' || 
            peerConnection.connectionState === 'failed' ||
            peerConnection.connectionState === 'closed') {
          this.handleDisconnectedPeer(remoteUserId);
        }
      };
      
      // Handle ICE connection state changes
      peerConnection.oniceconnectionstatechange = (event) => {
        console.log(`ICE connection state change with ${remoteUserId}:`, peerConnection.iceConnectionState);
      };
      
      // Handle when remote streams are added
      peerConnection.ontrack = (event) => {
        console.log("Remote track received from", remoteUserId);
        
        if (event.streams && event.streams[0]) {
          this.remoteStreams[remoteUserId] = event.streams[0];
          
          if (this.onRemoteStreamHandler) {
            this.onRemoteStreamHandler(remoteUserId, event.streams[0]);
          }
        }
      };
      
      return peerConnection;
    } catch (error) {
      console.error("Error creating peer connection:", error);
      throw error;
    }
  }

  // Create and send an offer to a remote peer
  async createOffer(remoteUserId) {
    try {
      const peerConnection = this.peerConnections[remoteUserId] || 
                             await this.createPeerConnection(remoteUserId);
      
      // Create the offer
      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);
      
      // Send the offer to the remote peer via Firebase
      await this.sendSignalingMessage(remoteUserId, {
        type: 'offer',
        sdp: peerConnection.localDescription.toJSON(),
        from: this.userId,
        to: remoteUserId
      });
      
      return offer;
    } catch (error) {
      console.error("Error creating offer:", error);
      throw error;
    }
  }

  // Handle an incoming offer from a remote peer
  async handleOffer(remoteUserId, offer) {
    try {
      // Create peer connection if it doesn't exist
      const peerConnection = this.peerConnections[remoteUserId] || 
                             await this.createPeerConnection(remoteUserId);
      
      // Set the remote description from the offer
      const rtcSessionDescription = new RTCSessionDescription(offer.sdp);
      await peerConnection.setRemoteDescription(rtcSessionDescription);
      
      // Create an answer
      const answer = await peerConnection.createAnswer();
      await peerConnection.setLocalDescription(answer);
      
      // Send the answer back to the offering peer
      await this.sendSignalingMessage(remoteUserId, {
        type: 'answer',
        sdp: peerConnection.localDescription.toJSON(),
        from: this.userId,
        to: remoteUserId
      });
      
      return answer;
    } catch (error) {
      console.error("Error handling offer:", error);
      throw error;
    }
  }

  // Handle an incoming answer to our offer
  async handleAnswer(remoteUserId, answer) {
    try {
      const peerConnection = this.peerConnections[remoteUserId];
      
      if (!peerConnection) {
        console.error("No peer connection found for user:", remoteUserId);
        return;
      }
      
      const rtcSessionDescription = new RTCSessionDescription(answer.sdp);
      await peerConnection.setRemoteDescription(rtcSessionDescription);
      
      console.log("Successfully set remote description from answer");
    } catch (error) {
      console.error("Error handling answer:", error);
      throw error;
    }
  }

  // Handle an incoming ICE candidate
  async handleIceCandidate(remoteUserId, iceCandidate) {
    try {
      const peerConnection = this.peerConnections[remoteUserId];
      
      if (!peerConnection) {
        console.error("No peer connection found for user:", remoteUserId);
        return;
      }
      
      const candidate = new RTCIceCandidate(iceCandidate.candidate);
      await peerConnection.addIceCandidate(candidate);
      
      console.log("Successfully added ICE candidate");
    } catch (error) {
      console.error("Error handling ICE candidate:", error);
      throw error;
    }
  }

  // Send a signaling message via Firebase
  async sendSignalingMessage(remoteUserId, message) {
    try {
      const messageId = `${this.userId}_${remoteUserId}_${Date.now()}`;
      const messageRef = doc(this.signalingCollection, messageId);
      
      await setDoc(messageRef, {
        ...message,
        groupId: this.groupId,
        timestamp: serverTimestamp()
      });
      
      console.log("Sent signaling message:", message.type);
      
      // Cleanup message after a delay (3 seconds)
      setTimeout(async () => {
        try {
          await deleteDoc(messageRef);
        } catch (error) {
          console.error("Error cleaning up signaling message:", error);
        }
      }, 3000);
    } catch (error) {
      console.error("Error sending signaling message:", error);
      throw error;
    }
  }

  // Set up listener for incoming signaling messages
  setupSignalingListener() {
    // Create query for messages directed to this user in this group
    const q = query(
      this.signalingCollection,
      where('to', '==', this.userId),
      where('groupId', '==', this.groupId)
    );
    
    // Subscribe to signaling messages
    this.signalingUnsubscribe = onSnapshot(q, async (snapshot) => {
      snapshot.docChanges().forEach(async (change) => {
        if (change.type === 'added') {
          const message = change.doc.data();
          
          // Handle the message based on its type
          if (message.from && message.from !== this.userId) {
            console.log("Received signaling message:", message.type, "from", message.from);
            
            switch (message.type) {
              case 'offer':
                await this.handleOffer(message.from, message);
                break;
              case 'answer':
                await this.handleAnswer(message.from, message);
                break;
              case 'ice-candidate':
                await this.handleIceCandidate(message.from, message);
                break;
              case 'disconnect':
                this.handleDisconnectedPeer(message.from);
                break;
              default:
                console.warn("Unknown message type:", message.type);
            }
          }
        }
      });
    }, (error) => {
      console.error("Error in signaling listener:", error);
    });
  }

  // Handle a disconnected peer
  handleDisconnectedPeer(remoteUserId) {
    console.log("Handling disconnected peer:", remoteUserId);
    
    // Close and remove the connection
    if (this.peerConnections[remoteUserId]) {
      this.peerConnections[remoteUserId].close();
      delete this.peerConnections[remoteUserId];
    }
    
    // Remove the stream reference
    if (this.remoteStreams[remoteUserId]) {
      delete this.remoteStreams[remoteUserId];
    }
    
    // Call the handler if provided
    if (this.onRemoteUserDisconnectedHandler) {
      this.onRemoteUserDisconnectedHandler(remoteUserId);
    }
  }

  // End the call and cleanup resources
  async endCall() {
    try {
      if (!this.callActive) return;
      
      // Notify other participants that we're leaving
      const roomSnapshot = await getDoc(this.roomRef);
      
      if (roomSnapshot.exists()) {
        const roomData = roomSnapshot.data();
        const participants = roomData.activeParticipants || [];
        
        // Send disconnect messages to all other participants
        for (const participantId of participants) {
          if (participantId !== this.userId) {
            await this.sendSignalingMessage(participantId, {
              type: 'disconnect',
              from: this.userId,
              to: participantId
            });
          }
        }
        
        // Remove the current user from active participants using arrayRemove
        await updateDoc(this.roomRef, {
          activeParticipants: arrayRemove(this.userId)
        });
        
        // Check if room is now empty
        const updatedRoomSnapshot = await getDoc(this.roomRef);
        if (updatedRoomSnapshot.exists() && 
            (updatedRoomSnapshot.data().activeParticipants?.length === 0 || 
             !updatedRoomSnapshot.data().activeParticipants)) {
          // Delete the room if empty
          await deleteDoc(this.roomRef);
        }
      }
      
      // Close all peer connections
      Object.keys(this.peerConnections).forEach(userId => {
        this.peerConnections[userId].close();
      });
      
      // Clear references
      this.peerConnections = {};
      this.remoteStreams = {};
      
      // Stop all local media tracks
      if (this.localStream) {
        this.localStream.getTracks().forEach(track => track.stop());
        this.localStream = null;
      }
      
      // Unsubscribe from signaling listener
      if (this.signalingUnsubscribe) {
        this.signalingUnsubscribe();
        this.signalingUnsubscribe = null;
      }
      
      this.callActive = false;
      console.log("Call ended and resources cleaned up");
    } catch (error) {
      console.error("Error ending call:", error);
      throw error;
    }
  }

  // Toggle local audio
  toggleAudio(enabled) {
    if (this.localStream) {
      const audioTracks = this.localStream.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = enabled;
      });
      return audioTracks.length > 0 ? audioTracks[0].enabled : false;
    }
    return false;
  }

  // Toggle local video
  toggleVideo(enabled) {
    if (this.localStream) {
      const videoTracks = this.localStream.getVideoTracks();
      videoTracks.forEach(track => {
        track.enabled = enabled;
      });
      return videoTracks.length > 0 ? videoTracks[0].enabled : false;
    }
    return false;
  }

  // Get active participants
  async getActiveParticipants() {
    try {
      const roomSnapshot = await getDoc(this.roomRef);
      
      if (roomSnapshot.exists()) {
        const roomData = roomSnapshot.data();
        return roomData.activeParticipants || [];
      }
      
      return [];
    } catch (error) {
      console.error("Error getting active participants:", error);
      throw error;
    }
  }
}